import {
  inject,
  signal,
  computed,
  Injector,
  OnInit,
  Component,
  ViewChild,
  DestroyRef,
  AfterViewInit,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';

import {
  DialogCloseOptions,
  DialogCloseReason,
  SuiDialogTabsComponent,
  SuiDialogLeftHeaderSlot,
  SuiDialogLeftContentSlot,
  SuiDialogRightHeaderSlot,
  SuiDialogRightContentSlot,
  SuiDialogRightFooterSlot,
} from '@sui/dialog';

import { ObjectPickerDialogMetadataService } from 'src/app/objects/services/object-picker-dialog-metadata.service';

import { DataObjectListComponent } from '../../components/dataobject-list/dataobject-list.component';
import { PrincipalObjectListComponent } from './principal-object-list/principal-object-list.component';

import {
  NomObjectClass,
  NomObjectTypes,
  PopupType,
} from 'src/app/core/constants';
import {
  DataObject,
  ObjectDefinition,
  Workspace,
} from 'src/app/core/models/core.models';
import { TabDialogBase } from 'src/app/shared/abstract/tab-dialog-base';
import { injectSearchManager, SearchableDialog } from 'src/app/shared/utils';
import { getIconForDataObject } from 'src/app/shared/utils/object-utils';

import { injectUserObjectSearchQuery } from 'src/app/api/queries/object.queries';
import { UserObjectSearchCriteria } from 'src/app/api/services/user-api.service';
import { UserWorkspacesService } from 'src/app/core/services/user-workspaces.service';

import { combineLatest, map, tap } from 'rxjs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SearchInputComponent } from '../../../shared/components/search-input/search-input.component';
import { DialogTabNavComponent } from '../../../shared/components/dialog-tab-nav/dialog-tab-nav.component';
import { SuiButtonComponent } from '@sui/button';

interface DialogData {
  selectedObjectIds: Map<string, ObjectDefinition>;
  objectClassIds: Array<number>;
  objectTypeIds: Array<number>;
  companyId: string | null | undefined;
  useObjectMap: boolean;
  singleSelectionActive: boolean;
  maxSelectedCount: number;
  showSelectedOnly: boolean;
  popupType: PopupType;
  /** Principal ids the picker never offers (owner flow: the current owner). */
  lockedUserIds?: string[];
}

interface DialogResult {}

@Component({
  selector: 'app-picker-dataobjects-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './picker-dataobjects-dialog.component.html',
  styleUrl: './picker-dataobjects-dialog.component.scss',
  imports: [
    SuiDialogTabsComponent,
    SuiDialogLeftHeaderSlot,
    FaIconComponent,
    SuiDialogLeftContentSlot,
    SearchInputComponent,
    DialogTabNavComponent,
    SuiDialogRightHeaderSlot,
    SuiButtonComponent,
    SuiDialogRightContentSlot,
    SuiDialogRightFooterSlot,
    TranslatePipe,
  ],
})
export class PickerDataobjectsDialogComponent
  extends TabDialogBase<DialogData, DialogResult>
  implements SearchableDialog, OnInit, AfterViewInit
{
  protected destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private userWorkspaces = inject(UserWorkspacesService);

  // * the search behind the workspace tab counts. Null until the user types, which
  // * keeps the query disabled - see `setupTabs`.
  private readonly searchCriteria = signal<UserObjectSearchCriteria | null>(
    null,
  );
  private readonly searchQuery = injectUserObjectSearchQuery(
    this.searchCriteria,
  );
  private objectPickerDialogMetadataService = inject(
    ObjectPickerDialogMetadataService,
  );

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;
  private searchManager = injectSearchManager({ debounceTime: 150 });
  private activeTabId$ = toObservable(this.tabManager.activeTabId);

  protected translateService: TranslateService;

  protected searchEnabled = true;
  public outputObjectIds: any;
  public isOverLimit = false;

  objectActionIcon: IconDefinition | IconProp;
  objectIcon: IconDefinition | IconProp;

  objectActionTitle = '';
  objectTitle = '';
  objectTitleDefault = this.translateService.instant(
    'popup.object-title.untitled',
  );

  get searchString$() {
    return this.searchManager.searchString$;
  }

  get selectedObjectIds() {
    return this.dialogData.selectedObjectIds ?? new Map();
  }

  get objectClassIds() {
    return this.dialogData.objectClassIds ?? [NomObjectClass.FEED];
  }

  get objectTypeIds() {
    return this.dialogData.objectTypeIds ?? [];
  }

  get companyId() {
    return this.dialogData.companyId ?? undefined;
  }

  get useObjectMap() {
    return this.dialogData.useObjectMap ?? false;
  }

  get singleSelectionActive() {
    return this.dialogData.singleSelectionActive ?? false;
  }

  get maxSelectedCount() {
    return this.dialogData.maxSelectedCount ?? 0;
  }

  get showSelectedOnly() {
    return this.dialogData.showSelectedOnly ?? false;
  }

  get popupType() {
    return this.dialogData.popupType;
  }

  get lockedUserIds(): string[] {
    return this.dialogData.lockedUserIds ?? [];
  }

  onSearchInput = (value: string) => {
    this.searchManager.onSearchInput(value);
  };

  ngOnInit(): void {
    this.outputObjectIds = this.useObjectMap
      ? this.selectedObjectIds
      : Array.from(this.selectedObjectIds.keys());
    this.setupUIState();
    this.setupTabs();
  }

  ngAfterViewInit(): void {
    this.initializeBase();
    if (this.maxSelectedCount > 0) {
      this.isOverLimit = this.selectedObjectIds.size > this.maxSelectedCount;
    }

    this.tabManager.activeFormValueChanges$
      .pipe(
        tap((formData) => {
          this.dataChangeSubscription();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  dataChangeSubscription(): void {
    const activeTabId = this.tabManager.activeTabId();
    const component = this.tabManager.getTabComponent(activeTabId);
    component.componentDataChange
      .pipe(
        tap((data: Map<string, ObjectDefinition>) => {
          const keysIterator = data.keys();
          if (this.useObjectMap) {
            this.outputObjectIds = Array.from(keysIterator).map((key) => {
              return {
                object_id: key,
                object_class_id: data.get(key).object_class_id,
              };
            });
          } else {
            this.outputObjectIds = Array.from(keysIterator);
          }

          if (this.maxSelectedCount > 0) {
            this.isOverLimit = data.size > this.maxSelectedCount;
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private setupUIState(): void {
    const dialogMetadata =
      this.objectPickerDialogMetadataService?.getPopupMetadata(this.popupType);

    if (dialogMetadata) {
      this.objectActionIcon =
        this.objectPickerDialogMetadataService?.getActionIcon(dialogMetadata);
      this.objectActionTitle =
        this.objectPickerDialogMetadataService?.getActionText(dialogMetadata);
    } else {
      this.objectActionTitle = this.translateService.instant(
        'picker-dataobjects-dialog.title.action',
      );
    }
  }

  private setupTabs(): void {
    const hasFeeds = this.objectClassIds.includes(NomObjectClass.FEED);
    const hasDashboards = this.objectClassIds.includes(
      NomObjectClass.DASHBOARD,
    );
    const hasUsers = this.objectClassIds.includes(NomObjectClass.USER);
    const hasGroups = this.objectClassIds.includes(NomObjectClass.USER_GROUP);
    const hasUsersAndGroups = hasUsers && hasGroups;

    if (hasFeeds && !hasUsersAndGroups) {
      const hasDimensions = this.objectTypeIds.includes(
        NomObjectTypes.SEARCH_DIMENSION,
      );
      if (hasDimensions) {
        this.objectIcon = getIconForDataObject(
          NomObjectClass.FEED,
          NomObjectTypes.SEARCH_DIMENSION,
        );
        this.objectTitle = this.translateService.instant(
          'picker-dataobjects.object-title.select-from-available-dimensions',
        );
      } else {
        this.objectIcon = getIconForDataObject(NomObjectClass.FEED);
        this.objectTitle = this.translateService.instant(
          'picker-dataobjects.object-title.select-from-available-feeds',
        );
      }
    } else if (hasUsersAndGroups) {
      this.objectIcon = getIconForDataObject(NomObjectClass.USER_GROUP);
      this.objectTitle = this.translateService.instant(
        'picker-dataobjects.object-title.select-from-available-principals',
      );
    } else if (hasUsers) {
      this.objectIcon = getIconForDataObject(NomObjectClass.USER);
      this.objectTitle = this.translateService.instant(
        'picker-dataobjects.object-title.select-from-available-users',
      );
    } else {
      this.objectIcon = getIconForDataObject(NomObjectClass.USER_GROUP);
      this.objectTitle = this.translateService.instant(
        'picker-dataobjects.object-title.select-from-available-user-groups',
      );
    }

    const baseInputs = {
      searchString$: this.searchString$,
      workspace: null,
      selectedObjectIds: this.selectedObjectIds,
      objectTypeIds: this.objectTypeIds,
      objectClassIds: this.objectClassIds,
      singleSelectionActive: this.singleSelectionActive,
      lockedUserIds: this.lockedUserIds,
    };

    this.tabs = [];

    // Special case: both users and groups
    if (hasUsers && hasGroups) {
      // ! User Groups - Not Here
      this.tabs.push({
        id: 'all',
        label: this.translateService.instant('picker-dataobjects.tab.all'),
        icon: ['fas', 'user'],
        component: PrincipalObjectListComponent,
        inputs: {
          ...baseInputs,
          selectorType: 'users,groups',
          // ??? - should we pass companyId here? - OR it will be the user.company_id?
        },
      });
    } else if (hasFeeds || hasDashboards) {
      this.tabs.push({
        id: 'all-workspaces',
        label: this.translateService.instant(
          'picker-dataobjects.tab.all-workspaces',
        ),
        icon: ['fas', 'folder-open'],
        component: DataObjectListComponent,
        inputs: {
          ...baseInputs,
          showSelectedOnly: this.showSelectedOnly,
          maxSelectedCount: this.maxSelectedCount,
          tabId: 'all-workspaces',
          activeTabId$: this.activeTabId$,
        },
      });
    }

    // * Users only tab (added only if not hasUsers && hasGroups)
    if (hasUsers && (!hasGroups || hasFeeds)) {
      this.tabs.push({
        id: 'users',
        label: this.translateService.instant('picker-dataobjects.tab.users'),
        icon: ['fas', 'user'],
        component: PrincipalObjectListComponent,
        inputs: {
          ...baseInputs,
          companyId: this.companyId,
        },
      });
    }

    if (hasGroups) {
      // ! User Groups
      this.tabs.push({
        id: 'user-groups',
        label: this.translateService.instant(
          'picker-dataobjects.tab.user-groups',
        ),
        icon: ['fas', 'user-group'],
        component: PrincipalObjectListComponent,
        inputs: {
          ...baseInputs,
          objectClassIds: [NomObjectClass.USER_GROUP],
          showSelectedOnly: this.showSelectedOnly,
          maxSelectedCount: this.maxSelectedCount,
          // ??? - should we pass companyId here? - OR it will be the user.company_id?
        },
      });
    }

    if (hasFeeds || hasDashboards) {
      // * the tab match counts run the search they count: same criteria as the "all"
      // * tab (no workspace filter), one request per distinct term, disabled while
      // * the box is empty (no search means every workspace, no count)
      this.searchString$
        .pipe(
          tap((searchString) =>
            this.searchCriteria.set(
              searchString?.trim()
                ? {
                    query: searchString,
                    workspaceId: null,
                    objectClassIds: this.objectClassIds,
                    objectTypeIds: this.objectTypeIds,
                  }
                : null,
            ),
          ),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();

      // * the workspace list resolves once per dialog open; the counts recompute on
      // * every search
      combineLatest([
        this.userWorkspaces.sortedWorkspaces$(),
        toObservable(
          computed(() => ({
            objects: this.searchQuery.data() ?? [],
            searchString: this.searchCriteria()?.query ?? '',
          })),
          { injector: this.injector },
        ),
      ])
        .pipe(
          map(([workspaces, { objects, searchString }]) =>
            withMatchCounts(workspaces, objects, searchString),
          ),
          tap((workspaces) => {
            this.tabs = this.tabs.filter(
              (tab) =>
                tab.component !== DataObjectListComponent ||
                tab.inputs['workspace'] === null,
            );
            workspaces
              .slice()
              .sort((a, b) =>
                (a.title ?? '')
                  .toString()
                  .toLowerCase()
                  .localeCompare((b.title ?? '').toString().toLowerCase()),
              )
              .forEach((workspace) => {
                // * keyed by identity, not position: search-driven rebuilds must
                // * not alias a cached tab component to a different workspace
                const tabId = 'workspace-' + workspace.id;
                this.tabs.push({
                  id: tabId,
                  label:
                    (workspace.title ??
                      this.translateService.instant(
                        'dataobject-picker.label.untitled-workspace',
                      )) +
                    (workspace.match_count > 0
                      ? ` (${workspace.match_count})`
                      : ''),
                  component: DataObjectListComponent,
                  icon: ['fas', 'folder'],
                  inputs: {
                    ...baseInputs,
                    workspace,
                    tabId,
                    activeTabId$: this.activeTabId$,
                  },
                });
              });

            // ! The workspace list resolves through Angular Query, i.e. always
            // ! asynchronously - these tabs land after initializeBase() captured
            // ! the array in ngAfterViewInit. Reconcile the manager or the new
            // ! tabs render in the sidebar but selectTab() does not know them.
            this.tabManager.syncTabs(this.tabs);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  protected override performSave(): DialogCloseOptions<DialogResult> {
    return { reason: DialogCloseReason.Save, data: this.outputObjectIds };
  }

  save(): void {
    if (!this.validateAndShow() || this.isOverLimit) {
      return;
    }

    const result = this.performSave();
    this.dirtyHandler.closeImmediately(result);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}

/** Workspaces annotated with how many of the current search results live in them. */
function withMatchCounts(
  workspaces: Workspace[],
  filteredObjects: DataObject[],
  searchString: string,
): Array<Workspace & { match_count: number }> {
  if (!workspaces?.length) return [];

  // * with no active search every workspace is shown, uncounted
  if (!searchString || searchString.trim().length === 0) {
    return workspaces.map((w) => ({ ...w, match_count: 0 }));
  }

  const matchCounts = filteredObjects.reduce<Record<string, number>>(
    (acc, obj) => {
      const id = obj?.workspace_id;
      if (id !== null && id !== undefined) {
        const key = String(id);
        acc[key] = (acc[key] || 0) + 1;
      }
      return acc;
    },
    {},
  );

  // * while searching, only workspaces with matches are offered as tabs
  return workspaces
    .filter((w) => matchCounts[String(w.id)])
    .map((w) => ({
      ...w,
      match_count: matchCounts[String(w.id)] || 0,
    }));
}
